<div class="doc-content">
  <h1>Test scenarios</h1>

  <section class="doc-section">
    <h2>Summary</h2>
    <p>
      The section explains mandatory test scenarios that Partner has to successfully complete in order to pass Quality Assurance testing on sandbox environment.
      Optional test scenarios may support the integration process to assure the best results.
    </p>
  </section>

  <section class="doc-section">
    <h2 id="mandatory">Mandatory scenarios</h2>

    <!-- Test Case 1 -->
    <div class="test-case mandatory">
        <h3>API User can create an application with agent matching by ID</h3>

        <div class="section">
            <div class="section-title">Scenario:</div>
            <p>
                <strong>GIVEN</strong> I'm a Broker API User sending POST request to Create Application endpoint<br>
                <strong>AND</strong> that request has valid bearer token<br>
                <strong>AND</strong> the request contains valid application DTO and agent ID<br>
                <strong>WHEN</strong> such request is sent<br>
                <strong>THEN</strong> application, account and contact are created for the current Broker<br>
                <strong>AND</strong> the application has Draft status in Broker Portal<br>
                <strong>AND</strong> 201 HTTP status is returned
            </p>
        </div>

        <div class="section">
            <div class="section-title">Preconditions:</div>
            <ul>
                <li>Broker API user is authenticated with a valid bearer token.</li>
                <li>A valid agent ID exists.</li>
            </ul>
        </div>

        <div class="section">
            <div class="section-title">Test Steps:</div>
            <p>Send a POST request to create an application:</p>
            <div class="endpoint">
                <span class="http-method">POST:</span> https://partner.api.ideafinancial.net/v1/applications<br>
                Authorization: Bearer &#123;access_token&#125;<br>
                Content-Type: application/json
            </div>
        </div>

        <div class="section">
            <div class="section-title">Expected Results:</div>
            <ul class="expected-results">
                <li>201 Created status.</li>
                <li>Application, account, and contact are created for the Broker.</li>
                <li>Application has Draft status in the Broker Portal.</li>
            </ul>
        </div>
    </div>

    <!-- Test Case 2 -->
    <div class="test-case mandatory">
        <h3>API User can update application in Submission Incomplete status (or Draft)</h3>

        <div class="section">
            <div class="section-title">Scenario:</div>
            <p>
                <strong>GIVEN</strong> I'm a Broker API User sending PATCH request to application update endpoint<br>
                <strong>AND</strong> that request has valid bearer token<br>
                <strong>AND</strong> it contains valid application ID<br>
                <strong>AND</strong> valid DTO with at least one field with the new value to be updated<br>
                <strong>AND</strong> the application is in Submission Incomplete<br>
                <strong>WHEN</strong> such request is sent<br>
                <strong>THEN</strong> updated fields could be verified in Broker Portal<br>
                <strong>AND</strong> 200 HTTP status is returned
            </p>
        </div>

        <div class="section">
            <div class="section-title">Preconditions:</div>
            <ul>
                <li>Application exists in Submission Incomplete status.</li>
                <li>Broker API user is authenticated.</li>
            </ul>
        </div>

        <div class="section">
            <div class="section-title">Test Steps:</div>
            <p>Send a PATCH request to update the application:</p>
            <div class="endpoint">
                <span class="http-method">PATCH:</span> https://partner.api.ideafinancial.net/v1/applications/&#123;applicationId&#125;<br>
                Authorization: Bearer &#123;access_token&#125;<br>
                Content-Type: application/json
            </div>
        </div>

        <div class="section">
            <div class="section-title">Expected Results:</div>
            <ul class="expected-results">
                <li>200 OK status.</li>
                <li>Updated fields can be verified in the Broker Portal.</li>
            </ul>
        </div>
    </div>

    <!-- Test Case 3 -->
    <div class="test-case mandatory">
        <h3>Broker API User can upload files to application in Submission Incomplete status (or Draft)</h3>

        <div class="section">
            <div class="section-title">Scenario:</div>
            <p>
                <strong>GIVEN</strong> I'm a Broker API User sending POST request to application files upload endpoint<br>
                <strong>AND</strong> that request has valid bearer token<br>
                <strong>AND</strong> the request contains valid upload files to application DTO<br>
                <strong>AND</strong> the application is in Submission Incomplete status<br>
                <strong>WHEN</strong> such request is sent<br>
                <strong>THEN</strong> the application file is uploaded in Broker Portal<br>
                <strong>AND</strong> visible under correct section in submission form<br>
                <strong>AND</strong> 201 HTTP status is returned
            </p>
        </div>

        <div class="section">
            <div class="section-title">Preconditions:</div>
            <ul>
                <li>Application exists in Submission Incomplete status.</li>
                <li>Broker API user is authenticated.</li>
            </ul>
        </div>

        <div class="section">
            <div class="section-title">Test Steps:</div>
            <p>Send a POST request to upload application files:</p>
            <div class="endpoint">
                <span class="http-method">POST:</span> https://partner.api.ideafinancial.net/v1/applications/&#123;applicationId&#125;/files<br>
                Authorization: Bearer &#123;access_token&#125;<br>
                Content-Type: multipart/form-data
            </div>
        </div>

        <div class="section">
            <div class="section-title">Expected Results:</div>
            <ul class="expected-results">
                <li>201 Created status.</li>
                <li>File is uploaded and visible in the correct section of the submission form in the Broker Portal.</li>
            </ul>
        </div>
    </div>

    <!-- Test Case 4 -->
    <div class="test-case mandatory">
        <h3>Broker API User can push an application in submission incomplete</h3>

        <div class="section">
            <div class="section-title">Scenario:</div>
            <p>
                <strong>GIVEN</strong> I'm a Broker API User sending POST request to Applications Push endpoint<br>
                <strong>AND</strong> the request has valid bearer token<br>
                <strong>AND</strong> the path contains existing application ID<br>
                <strong>AND</strong> application is in status Submission Incomplete<br>
                <strong>AND</strong> application has required attachment(s) of type: bank statements and application<br>
                <strong>WHEN</strong> such request is sent<br>
                <strong>THEN</strong> success 204 HTTP status is returned<br>
                <strong>AND</strong> new status Processing could be verified in Broker Portal<br>
                <strong>AND</strong> application files are marked as permanent<br>
                <strong>AND</strong> an email is sent <strong>(only in Production environment)</strong>
            </p>
        </div>

        <div class="section">
            <div class="section-title">Preconditions:</div>
            <ul>
                <li>Application exists in Submission Incomplete status.</li>
                <li>Broker API user is authenticated.</li>
                <li>Application has required bank statement and signed application attachments.</li>
            </ul>
        </div>

        <div class="section">
            <div class="section-title">Test Steps:</div>
            <p>Send a POST request to submit the application:</p>
            <div class="endpoint">
                <span class="http-method">POST:</span> https://partner.api.ideafinancial.net/v1/applications/&#123;applicationId&#125;/push<br>
                Authorization: Bearer &#123;access_token&#125;
            </div>
            <p>Validate the response.</p>
        </div>

        <div class="section">
            <div class="section-title">Expected Results:</div>
            <ul class="expected-results">
                <li>204 No Content status.</li>
                <li>Application status updates to Processing in the Broker Portal.</li>
                <li>Application files are marked as permanent.</li>
                <li>Email notification is sent (only in Production).</li>
            </ul>
        </div>
    </div>

    <!-- Test Case 5 -->
    <div class="test-case mandatory">
        <h3>Broker API User can upload closing requirements when the submission is in offer status</h3>

        <div class="section">
            <div class="section-title">Scenario:</div>
            <p>
                <strong>GIVEN</strong> I'm a Broker API User sending POST request to checkout requirement files upload endpoint<br>
                <strong>AND</strong> that request has valid bearer token<br>
                <strong>AND</strong> the request contains valid upload files to application DTO & valid checkout requirement ID<br>
                <strong>AND</strong> the application is in offer status<br>
                <strong>AND</strong> application consists of multiple offers, checkout requirements, next steps<br>
                <strong>WHEN</strong> such request is sent<br>
                <strong>THEN</strong> the requirement file is uploaded in Broker Portal<br>
                <strong>AND</strong> visible under correct section in Broker Portal as temporary<br>
                <strong>AND</strong> 201 HTTP status is returned
            </p>
        </div>

        <div class="section">
            <div class="section-title">Preconditions:</div>
            <ul>
                <li>Application exists in Offer status.</li>
                <li>Broker API user is authenticated.</li>
                <li>Application contains multiple offers, checkout requirements, and next steps.</li>
            </ul>
        </div>

        <div class="section">
            <div class="section-title">Test Steps:</div>
            <p>Send a POST request to upload checkout requirement files:</p>
            <div class="endpoint">
                <span class="http-method">POST:</span> https://partner.api.ideafinancial.net/v1/applications/&#123;applicationId&#125;/checkout-requirements/&#123;requirementId&#125;/upload<br>
                Authorization: Bearer &#123;access_token&#125;<br>
                Content-Type: multipart/form-data
            </div>
        </div>

        <div class="section">
            <div class="section-title">Expected Results:</div>
            <ul class="expected-results">
                <li>201 Created status.</li>
                <li>File is uploaded and visible in the correct section of the Broker Portal as temporary.</li>
            </ul>
        </div>
    </div>

    <!-- Test Case 6 -->
    <div class="test-case mandatory">
        <h3>Broker API User can accept an offer</h3>

        <div class="section">
            <div class="section-title">Scenario:</div>
            <p>
                <strong>GIVEN</strong> I'm a Broker API User sending PATCH request to Accept endpoint<br>
                <strong>AND</strong> the request has valid bearer token<br>
                <strong>AND</strong> the path contains existing application ID<br>
                <strong>AND</strong> application is in offer status and contains offers for term loan product<br>
                <strong>AND</strong> request consists of accepted status, finalRate, finalAmount and offer ID<br>
                <strong>WHEN</strong> such request is sent<br>
                <strong>THEN</strong> success 204 HTTP status is returned<br>
                <strong>AND</strong> updated offer fields finalRate, finalAmount could be verified<br>
                <strong>AND</strong> status of an offer is updated<br>
                <strong>AND</strong> any previously accepted offer return to pending status
            </p>
        </div>

        <div class="section">
            <div class="section-title">Preconditions:</div>
            <ul>
                <li>Application exists in Offer status.</li>
                <li>Broker API user is authenticated.</li>
                <li>Application contains offers for a term loan product.</li>
            </ul>
        </div>

        <div class="section">
            <div class="section-title">Test Steps:</div>
            <p>Send a PATCH request to accept an offer:</p>
            <div class="endpoint">
                <span class="http-method">PATCH:</span> https://partner.api.ideafinancial.net/v1/applications/&#123;applicationId&#125;/offers/&#123;offerId&#125;/acceptance<br>
                Authorization: Bearer &#123;access_token&#125;<br>
                Content-Type: application/json
            </div>
        </div>

        <div class="section">
            <div class="section-title">Expected Results:</div>
            <ul class="expected-results">
                <li>204 No Content status.</li>
                <li>Offer status updates to Accepted.</li>
                <li>finalRate and finalAmount fields are updated.</li>
                <li>Any previously accepted offer returns to Pending status.</li>
            </ul>
        </div>
    </div>

    <h2 id="optional">Optional scenarios</h2>

    <!-- Optional Test Case 1 -->
    <div class="test-case optional">
        <h3>User can Create API keys from Broker Portal</h3>

        <div class="section">
            <div class="section-title">Scenario:</div>
            <p>
                <strong>GIVEN</strong> I'm Broker Portal Admin user<br>
                <strong>AND</strong> I'm logged in Broker Portal<br>
                <strong>WHEN</strong> I navigate to Admin tab<br>
                <strong>AND</strong> click on New API KEY button<br>
                <strong>AND</strong> I input at least name<br>
                <strong>AND</strong> select status<br>
                <strong>AND</strong> confirm<br>
                <strong>THEN</strong> system generates user ID and client secret<br>
                <strong>AND</strong> displays it in a pop-up
            </p>
        </div>
    </div>

    <!-- Optional Test Case 2 -->
    <div class="test-case optional">
        <h3>Broker gets access (single key)</h3>

        <div class="section">
            <div class="section-title">Scenario:</div>
            <p>
                <strong>GIVEN</strong> I'm Broker API User<br>
                <strong>AND</strong> I have only one pair of keys (ID and client secret)<br>
                <strong>AND</strong> my API is enabled <strong>AND</strong> my keys are active<br>
                <strong>WHEN</strong> I send GET request with those keys at token endpoint<br>
                <strong>THEN</strong> system responds with success 200<br>
                <strong>AND</strong> returns access token which contains: Bearer type, expiration time and refresh token
            </p>
        </div>
    </div>

    <!-- Optional Test Case 3 -->
    <div class="test-case optional">
        <h3>Broker API User can create an agent matching by email with role in Broker Portal</h3>

        <div class="section">
            <div class="section-title">Scenario:</div>
            <p>
                <strong>GIVEN</strong> I'm a Broker API user sending POST request to create agent endpoint<br>
                <strong>AND</strong> that request has valid bearer token<br>
                <strong>AND</strong> the request contains Broker Portal role parameter<br>
                <strong>AND</strong> the request contains Mobile Phone parameter<br>
                <strong>AND</strong> the agent matching by email does NOT exist<br>
                <strong>WHEN</strong> such request is sent<br>
                <strong>THEN</strong> agent is created<br>
                <strong>AND</strong> an invitation email to Broker Portal is sent to agent's email <strong>(only in Production environment)</strong><br>
                <strong>AND</strong> 201 HTTP status is returned with Agent's ID
            </p>
        </div>
    </div>

    <!-- Optional Test Case 4 -->
    <div class="test-case optional">
        <h3>Broker API User can update an agent</h3>

        <div class="section">
            <div class="section-title">Scenario:</div>
            <p>
                <strong>GIVEN</strong> I'm a Broker API user sending PATCH request to update agent endpoint<br>
                <strong>AND</strong> that request has valid bearer token<br>
                <strong>AND</strong> the request contains valid agent ID<br>
                <strong>AND</strong> the request changes only selected fields (e.g. lastName, email)<br>
                <strong>WHEN</strong> such request is sent<br>
                <strong>THEN</strong> agent is updated in Broker Portal<br>
                <strong>AND</strong> 200 HTTP status is returned
            </p>
        </div>
    </div>

    <!-- Optional Test Case 5 -->
    <div class="test-case optional">
        <h3>An error is returned when the Broker API User try to push an application with no attachments</h3>

        <div class="section">
            <div class="section-title">Scenario:</div>
            <p>
                <strong>GIVEN</strong> I'm a Broker API User sending POST request to Applications Push endpoint<br>
                <strong>AND</strong> the request has valid bearer token<br>
                <strong>AND</strong> the path contains existing application ID<br>
                <strong>AND</strong> application is in status Draft<br>
                <strong>AND</strong> application has no required attachment(s) of type: bank statements and application<br>
                <strong>WHEN</strong> such request is sent<br>
                <strong>THEN</strong> error 400 HTTP status is returned<br>
                <strong>AND</strong> no change in status could be verified<br>
                <strong>AND</strong> application files are NOT converted to permanent in Broker Portal
            </p>
        </div>
    </div>

    <!-- Optional Test Case 6 -->
    <div class="test-case optional">
        <h3>Broker API User can get application details in offer status</h3>

        <div class="section">
            <div class="section-title">Scenario:</div>
            <p>
                <strong>GIVEN</strong> I'm a Broker API User sending GET request to Get Application endpoint<br>
                <strong>AND</strong> the request has valid bearer token<br>
                <strong>AND</strong> the path contains existing application ID<br>
                <strong>AND</strong> application consists of multiple offers and checkout requirements<br>
                <strong>AND</strong> application is in Offer status<br>
                <strong>WHEN</strong> such request is sent<br>
                <strong>THEN</strong> success 200 HTTP status is returned<br>
                <strong>AND</strong> status, submission details<br>
                <strong>AND</strong> details of the offers and checkout requirements could be verified
            </p>
        </div>
    </div>

    <!-- Optional Test Case 7 -->
    <div class="test-case optional">
        <h3>Broker API User can mark application as Not interested</h3>

        <div class="section">
            <div class="section-title">Scenario:</div>
            <p>
                <strong>GIVEN</strong> I'm a Broker API User sending POST request to Not Interested endpoint<br>
                <strong>AND</strong> the request has valid bearer token & application ID<br>
                <strong>AND</strong> application is in status offer<br>
                <strong>AND</strong> payload content consists of notInterestedReason: Other<br>
                <strong>AND</strong> additionalComment free text comment<br>
                <strong>WHEN</strong> such request is sent<br>
                <strong>THEN</strong> success 204 HTTP status is returned<br>
                <strong>AND</strong> new status Not interested could be verified in Broker Portal
            </p>
        </div>
    </div>

    <!-- Optional Test Case 8 -->
    <div class="test-case optional">
        <h3>Broker API User can reactivate an application</h3>

        <div class="section">
            <div class="section-title">Scenario:</div>
            <p>
                <strong>GIVEN</strong> I'm a Broker API User sending POST request to reactivate<br>
                <strong>AND</strong> the request has valid bearer token & existing application ID<br>
                <strong>AND</strong> the application's last-previous status was e.g. offer<br>
                <strong>AND</strong> application is in status Not interested<br>
                <strong>WHEN</strong> such request is sent<br>
                <strong>THEN</strong> success 204 HTTP status is returned<br>
                <strong>AND</strong> the application status returned to the last-previous offer
            </p>
        </div>
    </div>

    <!-- Optional Test Case 9 -->
    <div class="test-case optional">
        <h3>Broker API User can get competitors list</h3>

        <div class="section">
            <div class="section-title">Scenario:</div>
            <p>
                <strong>GIVEN</strong> I'm a Broker API User sending GET request to competitors endpoint<br>
                <strong>AND</strong> that request has valid bearer token<br>
                <strong>WHEN</strong> such request is sent<br>
                <strong>THEN</strong> the competitors list is displayed<br>
                <strong>AND</strong> 200 HTTP status is returned
            </p>
        </div>
    </div>

    <!-- Optional Test Case 10 -->
    <div class="test-case optional">
        <h3>Broker API User can get not interested reasons list</h3>

        <div class="section">
            <div class="section-title">Scenario:</div>
            <p>
                <strong>GIVEN</strong> I'm a Broker API User sending GET request to not interested reasons endpoint<br>
                <strong>AND</strong> that request has valid bearer token<br>
                <strong>WHEN</strong> such request is sent<br>
                <strong>THEN</strong> the not interested reasons list is displayed<br>
                <strong>AND</strong> 200 HTTP status is returned
            </p>
        </div>
    </div>

    <!-- Optional Test Case 11 -->
    <div class="test-case optional">
        <h3>An error is returned when the request is missing any of the mandatory fields</h3>

        <div class="section">
            <div class="section-title">Scenario:</div>
            <p>
                <strong>GIVEN</strong> I'm a Broker API user sending POST request to create agent endpoint<br>
                <strong>AND</strong> that request has valid bearer token<br>
                <strong>AND</strong> the request is missing any of the mandatory fields<br>
                <strong>AND</strong> the agent matching by email does NOT exist for the current broker<br>
                <strong>WHEN</strong> such request is sent<br>
                <strong>THEN</strong> 400 HTTP status is returned with the list of errors<br>
                <strong>AND</strong> agent is not created
            </p>
        </div>
    </div>

    <!-- Optional Test Case 12 -->
    <div class="test-case optional">
        <h3>An error is returned when the request has invalid bearer token</h3>

        <div class="section">
            <div class="section-title">Scenario:</div>
            <p>
              <strong>GIVEN</strong> I’m a Broker API User sending POST request to Applications Push endpoint<br>
              <strong>AND</strong> the request has invalid bearer token<br>
              <strong>AND</strong> the path contains existing application ID<br>
              <strong>AND</strong> application is in status draft<br>
              <strong>AND</strong> application has required attachment(s) of type: bank statements and application<br>
              <strong>WHEN</strong> such request is sent<br>
              <strong>THEN</strong> error 401 HTTP status is returned<br>
              <strong>AND</strong> no change in status could be verified<br>
              <strong>AND</strong> application files are NOT converted to permanent in Broker Portal
            </p>
        </div>
    </div>

    <!-- Optional Test Case 13 -->
    <div class="test-case optional">
        <h3>Validate the concurrent API request and response time when the API User try to create applications with agent matching by ID</h3>

        <div class="section">
            <div class="section-title">Scenario:</div>
            <p>
                <strong>GIVEN</strong> I'm a Broker API User sending concurrent POST request to Create Application endpoint<br>
                <strong>AND</strong> the requests have valid bearer token<br>
                <strong>AND</strong> the requests contain valid application DTO and same agent ID<br>
                <strong>WHEN</strong> such requests are sent<br>
                <strong>THEN</strong> applications with business and owners information are created for the Broker<br>
                <strong>AND</strong> the applications have Draft status in Broker Portal<br>
                <strong>AND</strong> 201 HTTP status is returned
            </p>
        </div>
    </div>
</section>
</div>
